import type { Edge, Node } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import config from '@/app.config';
import useEmitter from '@/hooks/use-emitter.ts';
import useFlowStore from '@/store/flow.ts';
import { AppEvent, type EventPayloads } from '@/types/event-emitter.types';
import { editorValueToPlainText } from '@/utils/editor-conversion';
import FlowParser from '@/utils/flow-parser.ts';
import { getCardHeight } from '@/utils/get-flow-card-height.ts';

export function useFlowBuilder() {
	const { canvas, cardSize } = useFlowStore(
		useShallow((state) => ({
			canvas: state.canvas,
			cardSize: state.cardSize
		}))
	);

	const [collapsedMap, setCollapsedMap] = useState<Record<string, boolean>>({});
	const [changedViewCards, setChangedViewCards] = useState<
		Record<string, boolean>
	>({});

	const { flowHeadings, flowItems, itemsDictionary, groupedItems } = useMemo(
		() => new FlowParser(canvas),
		[canvas]
	);

	const mentionOptions = useMemo(
		() =>
			flowItems.map((item) => ({
				value: editorValueToPlainText(item.source.title),
				id: item.source.idn,
				color: item.color
			})),
		[flowItems]
	);

	const typesOptions = useMemo(
		() =>
			flowHeadings.map((group) => ({
				label: group.source.title,
				value: group.source.idn
			})),
		[flowHeadings]
	);

	const nodes = useMemo(() => {
		const view = config.view[cardSize];
		const { width, gapY, gapX } = view.columns;
		const { marginTop } = config.view;

		const headingNodes: Node[] = flowHeadings.map((heading, groupIndex) => ({
			id: `type-${heading.source.idn}`,
			data: heading,
			position: {
				x: (width + gapY) * groupIndex,
				y: 0
			},
			type: 'columnHeading'
		}));

		const typeIndex = Object.fromEntries(
			flowHeadings.map((h, i) => [h.source.idn, i])
		);

		const itemNodes = Object.entries(groupedItems).flatMap(
			([type, columnItems]) => {
				const colIdx = typeIndex[type] ?? 0;
				const xBase = (width + gapY) * colIdx;

				return columnItems.map((item, i) => {
					const collapsed = collapsedMap[item.source.idn] ?? false;

					const cardHeight = getCardHeight(
						item,
						collapsedMap,
						changedViewCards
					);

					const prevItems = columnItems.slice(0, i);
					const y =
						marginTop +
						prevItems.reduce((sum, prev) => {
							const prevH = getCardHeight(prev, collapsedMap, changedViewCards);
							return sum + prevH + gapX;
						}, 0);

					return {
						id: item.source.idn,
						data: {
							...item,
							isCollapsed: collapsed,
							cardHeight
						},
						position: { x: xBase, y },
						width,
						height: cardHeight,
						type: 'flowCard'
					};
				});
			}
		);

		return [...headingNodes, ...itemNodes];
	}, [flowHeadings, cardSize, collapsedMap, changedViewCards, groupedItems]);

	const edges = useMemo<Edge[]>(() => {
		const result: Edge[] = [];
		flowItems.forEach((item) => {
			item.outgoingMentions.forEach((mention) => {
				result.push({
					id: `edge-${item.source.idn}-${mention.item_idn}`,
					sourceHandle: `${item.source.idn}-${mention.item_idn}`,
					targetHandle: `${mention.item_idn}-${item.source.idn}`,
					source: item.source.idn,
					target: mention.item_idn,
					style: { stroke: item.color },
					markerEnd: {
						type: MarkerType.Arrow,
						color: item.color,
						width: 24,
						height: 24
					}
				});
			});
		});
		return result;
	}, [flowItems]);

	const collapseSpecificCardListener = useCallback(
		({ cardId, isCollapsed }: EventPayloads[AppEvent.COLLAPSE_EXPAND_CARD]) => {
			setCollapsedMap((prev) => ({ ...prev, [cardId]: isCollapsed }));
		},
		[]
	);

	const expandAllCardsListener = useCallback(() => {
		setCollapsedMap(
			flowItems.reduce<Record<string, boolean>>((acc, item) => {
				acc[item.source.idn] = true;
				return acc;
			}, {})
		);
	}, [flowItems]);

	useEmitter(AppEvent.COLLAPSE_EXPAND_CARD, collapseSpecificCardListener);
	useEmitter(AppEvent.EXPAND_ALL_CARDS, expandAllCardsListener);
	useEmitter(AppEvent.COLLAPSE_ALL_CARDS, () => setCollapsedMap({}));
	useEmitter(AppEvent.CARD_VIEW_CHANGED, ({ cardId, isDescriptionView }) => {
		setChangedViewCards((prev) => ({
			...prev,
			[cardId]: !isDescriptionView
		}));
	});

	return {
		nodes,
		edges,
		mentionOptions,
		typesOptions,
		itemsDictionary
	};
}
